import _has from "lodash/has";
import _isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";
import React, { Component } from "react";
import Overridable from "react-overridable";
import { Divider, Message } from "semantic-ui-react";
import { invenioConfig } from "@config";
import DocumentTabs from "./DocumentTabs";
import { LOCATION_OBJECT_TOTAL_AMOUNT_KEY } from "@config/common";

class DocumentItems extends Component {
  itemStatus = (item) => ({
    canCirculate: () =>
      invenioConfig.ITEMS.canCirculateStatuses.includes(item.status),

    isForReference: () =>
      invenioConfig.ITEMS.referenceStatuses.includes(item.status),
  });

  get allPhysicalLocations() {
    const {
      documentDetails: {
        metadata: {
          items: { on_shelf: onShelf, hits: allItems },
        },
      },
    } = this.props;

    // Start with on_shelf items
    const locationEntries = Object.entries(onShelf);
    const allLocations = {};

    // Add all on_shelf items
    locationEntries.forEach(([locationName, internalLocations]) => {
      const internalLocationEntries = Object.entries(internalLocations);
      const allInternalLocations = {};

      internalLocationEntries.forEach(([internalLocationName, items]) => {
        if (internalLocationName === LOCATION_OBJECT_TOTAL_AMOUNT_KEY) return;

        if (!_isEmpty(items)) {
          allInternalLocations[internalLocationName] = [...items];
        }
      });

      if (!_isEmpty(allInternalLocations)) {
        allLocations[locationName] = allInternalLocations;
      }
    });

    // Add items that are on loan (not in on_shelf but in hits)
    if (allItems && Array.isArray(allItems)) {
      allItems.forEach((item) => {
        // Check if this item is already in on_shelf
        const isAlreadyIncluded = Object.values(allLocations).some(
          (internalLocs) =>
            Object.values(internalLocs).some((items) =>
              items.some((shelfItem) => shelfItem.pid === item.pid)
            )
        );

        // If not already included (meaning it's on loan), add it
        if (!isAlreadyIncluded && item.internal_location) {
          const locationName = item.internal_location.location.name;
          const internalLocationName = item.internal_location.name;

          if (!allLocations[locationName]) {
            allLocations[locationName] = {};
          }

          if (!allLocations[locationName][internalLocationName]) {
            allLocations[locationName][internalLocationName] = [];
          }

          // Mark this item as on loan
          const itemWithLoanStatus = {
            ...item,
            _isOnLoan: true,
          };

          allLocations[locationName][internalLocationName].push(
            itemWithLoanStatus
          );
        }
      });
    }

    return allLocations;
  }

  // Keep the original filtered version as backup/alternative
  get filteredLocations() {
    const {
      documentDetails: {
        metadata: {
          items: { on_shelf: onShelf },
        },
      },
    } = this.props;

    const locationEntries = Object.entries(onShelf);

    if (_isEmpty(locationEntries)) return {};

    // relevant means it only has items that have the status on shelf or for reference and arrays that are not empty

    const relevantLocations = {};

    locationEntries.forEach(([locationName, internalLocations]) => {
      const internalLocationEntries = Object.entries(internalLocations);

      const relevantInternalLocations = {};

      internalLocationEntries.forEach(([internalLocationName, items]) => {
        if (internalLocationName === LOCATION_OBJECT_TOTAL_AMOUNT_KEY) return;

        const relevantItems = items.filter(
          (item) =>
            this.itemStatus(item).canCirculate() ||
            this.itemStatus(item).isForReference()
        );

        const internalLocationHasRelevantItems = !_isEmpty(relevantItems);

        if (internalLocationHasRelevantItems) {
          relevantInternalLocations[internalLocationName] = relevantItems;
        }
      });

      const locationHasRelevantInternalLocations = !_isEmpty(
        relevantInternalLocations
      );

      if (locationHasRelevantInternalLocations) {
        relevantLocations[locationName] = relevantInternalLocations;
      }
    });

    return relevantLocations;
  }

  render() {
    const {
      documentDetails: {
        metadata: { items },
      },
      documentDetails,
    } = this.props;

    // Use allPhysicalLocations instead of filteredLocations to show ALL items
    const internalLocationsComponent = (
      <DocumentTabs
        locationsObject={this.allPhysicalLocations}
        documentDetails={documentDetails}
      />
    );

    const noItemsComponent = (
      <Message>
        <Message.Header>No copies</Message.Header>
        <p>
          There are no available copies in the library. Please, contact the
          library for more information.
        </p>
      </Message>
    );

    // Check if there are any physical items at all
    const hasCopies =
      _has(items, "on_shelf") && !_isEmpty(this.allPhysicalLocations);

    const componentToShow = hasCopies
      ? internalLocationsComponent
      : noItemsComponent;

    return (
      <>
        <Overridable
          id="DocumentDetails.DocumentItems.header"
          document={documentDetails}
        >
          <Divider horizontal>Where to find</Divider>
        </Overridable>
        {componentToShow}
      </>
    );
  }
}

DocumentItems.propTypes = {
  documentDetails: PropTypes.object.isRequired,
};

export default Overridable.component("DocumentItems", DocumentItems);
